import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["task_import_runs"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      taskImportRunsList: build.query<
        TaskImportRunsListApiResponse,
        TaskImportRunsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/`,
          params: {
            firm_id: queryArg.firmId,
          },
        }),
        providesTags: ["task_import_runs"],
      }),
      taskImportRunsCreate: build.mutation<
        TaskImportRunsCreateApiResponse,
        TaskImportRunsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/`,
          method: "POST",
          body: queryArg.createTaskImportRun,
        }),
        invalidatesTags: ["task_import_runs"],
      }),
      taskImportRunsRetrieve: build.query<
        TaskImportRunsRetrieveApiResponse,
        TaskImportRunsRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/task-import-runs/${queryArg.id}/` }),
        providesTags: ["task_import_runs"],
      }),
      taskImportRunsDetail: build.query<
        TaskImportRunsDetailApiResponse,
        TaskImportRunsDetailApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/${queryArg.id}/detail/`,
        }),
        providesTags: ["task_import_runs"],
      }),
      taskImportRunsMatterLinks: build.query<
        TaskImportRunsMatterLinksApiResponse,
        TaskImportRunsMatterLinksApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/${queryArg.id}/matters/`,
        }),
        providesTags: ["task_import_runs"],
      }),
      taskImportRunsTaskLinksForMatter: build.query<
        TaskImportRunsTaskLinksForMatterApiResponse,
        TaskImportRunsTaskLinksForMatterApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/${queryArg.id}/matters/${queryArg.matterLinkId}/tasks/`,
        }),
        providesTags: ["task_import_runs"],
      }),
      taskImportRunsGuessFirmCms: build.query<
        TaskImportRunsGuessFirmCmsApiResponse,
        TaskImportRunsGuessFirmCmsApiArg
      >({
        query: (queryArg) => ({
          url: `/api/task-import-runs/guess-firm-cms/`,
          params: {
            firm_id: queryArg.firmId,
          },
        }),
        providesTags: ["task_import_runs"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type TaskImportRunsListApiResponse =
  /** status 200 List of runs */ TaskImportRunSchema[];
export type TaskImportRunsListApiArg = {
  /** Optional firm filter; when omitted, returns all firms */
  firmId?: string;
};
export type TaskImportRunsCreateApiResponse =
  /** status 201 Created run */ TaskImportRunSchema;
export type TaskImportRunsCreateApiArg = {
  createTaskImportRun: CreateTaskImportRun;
};
export type TaskImportRunsRetrieveApiResponse =
  /** status 200  */ TaskImportRunSchema;
export type TaskImportRunsRetrieveApiArg = {
  id: string;
};
export type TaskImportRunsDetailApiResponse =
  /** status 200  */ TaskImportRunDetail;
export type TaskImportRunsDetailApiArg = {
  id: string;
};
export type TaskImportRunsMatterLinksApiResponse =
  /** status 200  */ TaskImportRunMatterLinkSchema;
export type TaskImportRunsMatterLinksApiArg = {
  id: string;
};
export type TaskImportRunsTaskLinksForMatterApiResponse =
  /** status 200  */ TaskImportRunTaskLinkSchemaList;
export type TaskImportRunsTaskLinksForMatterApiArg = {
  id: string;
  matterLinkId: string;
};
export type TaskImportRunsGuessFirmCmsApiResponse =
  /** status 200  */ GuessFirmCmsResponse;
export type TaskImportRunsGuessFirmCmsApiArg = {
  /** Firm id to guess CMS for */
  firmId: string;
};
export type ExternalCmsType =
  | "casepeer"
  | "clio"
  | "filevine"
  | "mycase"
  | "neos"
  | "litify"
  | "smartadvocate";
export type TaskImportRunStatus =
  | "PENDING"
  | "RUNNING"
  | "COMPLETED"
  | "FAILED"
  | "CANCELLED";
export type TaskImportRunPhase =
  | "EXTRACTING"
  | "LINKING_MATTERS"
  | "LINKING_TASKS"
  | "DONE";
export type FirmStatus =
  | "Active"
  | "Testing"
  | "Software Only"
  | "New Lead"
  | "Open"
  | "Closed";
export type FirmType = "Solo" | "Small" | "Medium" | "Enterprise";
export type TimeZone = "PST" | "MST" | "CST" | "EST" | "AKST" | "AST" | "HST";
export type CmsSystem =
  | "CASEpeer"
  | "Filevine"
  | "Litify"
  | "MyCase"
  | "Clio Manage"
  | "SmartAdvocate"
  | "PracticePanther"
  | "Needles"
  | "Neos"
  | "Merus"
  | "TrialWorks";
export type LeadSystem =
  | "LeadDocket"
  | "LawProNation"
  | "ClioGrow"
  | "Intaker"
  | "CASEpeer"
  | "Filevine"
  | "Litify"
  | "MyCase"
  | "Clio Manage"
  | "SmartAdvocate"
  | "PracticePanther"
  | "Needles"
  | "Neos"
  | "Merus"
  | "TrialWorks";
export type IntakePreferenceSection = {
  title: string;
  content: string;
};
export type QualificationAction = "accept" | "reject" | "escalate";
export type QualificationCriteria = {
  /** Maximum days from today for date of loss. None means no restriction. */
  date_of_loss_max_days?: number | null;
  /** List of 2-letter US state codes. Empty means all states accepted. */
  accepted_states?: string[];
  /** Per case type actions. Missing types use default action. */
  case_type_actions?: {
    [key: string]: QualificationAction;
  };
  /** Default action for unconfigured case types. */
  default_case_type_action?: QualificationAction;
};
export type IntakePreferences = {
  sections?: IntakePreferenceSection[];
  /** Qualification criteria for intake decision support. */
  qualification_criteria?: QualificationCriteria;
};
export type FirmSchema = {
  id: string;
  name: string;
  full_name?: string | null;
  email?: string | null;
  adobe_sign_email?: string | null;
  status?: FirmStatus;
  type?: FirmType;
  is_paying?: boolean;
  paying_since?: string | null;
  time_zone?: TimeZone;
  primary_state?: string | null;
  managing_paralegal_id?: (string | string) | null;
  cms_system?: CmsSystem;
  cms_link?: string | null;
  lead_system?: LeadSystem;
  lead_system_link?: string | null;
  intake_preferences: IntakePreferences;
  case_preferences?: string | null;
  commentary?: string | null;
  errors?: string | null;
  plan_to_derisk?: string | null;
  pre_filing_contingency_fee?: string | null;
  post_filing_contingency_fee?: string | null;
  sms_sender_number?: string | null;
  sms_sender_name?: string | null;
  fax?: string | null;
};
export type TaskImportRunSchema = {
  id: string | string;
  firm_id: string | string;
  cms_type: ExternalCmsType;
  status: TaskImportRunStatus;
  phase: TaskImportRunPhase;
  file_name?: string | null;
  file_url?: string | null;
  firm: FirmSchema;
};
export type CreateTaskImportRun = {
  firm_id: string | string;
  cms_type: ExternalCmsType;
  file: Blob;
};
export type MatterFacets = {
  matter_name: string;
  external_matter_url?: string | null;
  external_matter_id: string;
};
export type TaskImportRunMatterSummary = {
  id: string | string;
  run_id: string | string;
  matter_id?: (string | string) | null;
  matter_facets: MatterFacets;
  matched_task_count: number;
  unmatched_task_count: number;
};
export type TaskImportRunDetail = {
  run: TaskImportRunSchema;
  matter_links: TaskImportRunMatterSummary[];
  total_matched_tasks: number;
  total_unmatched_tasks: number;
  linked_matter_count: number;
  unlinked_matter_count: number;
};
export type TaskImportRunMatterLinkSchema = {
  id: string | string;
  run_id: string | string;
  matter_id?: (string | string) | null;
  matter_facets: MatterFacets;
};
export type TaskFacets = {
  task_name?: string | null;
  task_description?: string | null;
  task_due_date?: string | null;
  task_completed_date?: string | null;
};
export type TaskLinkResult = {
  /** The id of the task that was matched. None if no match was found. */
  task_id: string | null;
  /** The confidence in the match. scales from 0 to 1.
    0-0.2: No confidence
    0.2-0.4: Low confidence
    0.4-0.6: Medium confidence
    0.6-0.8: High confidence
    0.8-1: Very high confidence */
  confidence: number;
  /** The confidence in the match. scales from 0 to 1.
    0-0.2: No confidence
    0.2-0.4: Low confidence
    0.4-0.6: Medium confidence
    0.6-0.8: High confidence
    0.8-1: Very high confidence */
  no_match_confidence: number;
  /** The reasoning for the match. */
  reasoning: string;
};
export type TaskImportRunTaskLinkSchema = {
  id: string | string;
  matter_link_id: string | string;
  task_id?: (string | string) | null;
  task_facets: TaskFacets;
  link_result: TaskLinkResult;
};
export type TaskImportRunTaskLinkSchemaList = TaskImportRunTaskLinkSchema[];
export type GuessFirmCmsResponse = {
  firm_id: string | string;
  cms_type: ExternalCmsType;
};
export const {
  useTaskImportRunsListQuery,
  useTaskImportRunsCreateMutation,
  useTaskImportRunsRetrieveQuery,
  useTaskImportRunsDetailQuery,
  useTaskImportRunsMatterLinksQuery,
  useTaskImportRunsTaskLinksForMatterQuery,
  useTaskImportRunsGuessFirmCmsQuery,
} = injectedRtkApi;
